import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { tasksApi } from "@/lib/api";
import ContactPicker from "@/components/ContactPicker";
import { toast } from "sonner";
import { ExternalLink } from "lucide-react";

const empty = {
  name: "", description: "", status: "Not started", priority: "Normal",
  due_date: "", assignee_contact_id: "", project_id: "",
  reference_url: "", related_contact_id: "",
};

// Accept plain-language input like "example.com" and auto-add https:// so
// clicking the saved link always opens correctly. Non-URLs are rejected on
// save via a lightweight regex — no HTML input[type=url] because Safari
// requires a scheme for it to validate.
const URL_RX = /^https?:\/\/\S+$/i;
const normalizeUrl = (v) => {
  const s = (v || "").trim();
  if (!s) return "";
  if (URL_RX.test(s)) return s;
  if (/^[\w-]+(\.[\w-]+)+/.test(s)) return `https://${s}`;
  return s;
};

export default function TaskDialog({ open, setOpen, task, entityId, projects, onSaved }) {
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setForm(task ? { ...empty, ...task } : empty); }, [task, open]);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name.trim()) return toast.error("Task name is required");
    const ref = normalizeUrl(form.reference_url);
    if (ref && !URL_RX.test(ref)) return toast.error("Reference URL must be a valid http(s) URL");
    setSaving(true);
    try {
      const payload = { ...form, entity_id: entityId, reference_url: ref || null };
      if (!payload.project_id) payload.project_id = null;
      if (!payload.assignee_contact_id) payload.assignee_contact_id = null;
      if (!payload.related_contact_id) payload.related_contact_id = null;
      if (!payload.due_date) payload.due_date = null;
      if (task?.id) await tasksApi.update(task.id, payload);
      else await tasksApi.create(payload);
      toast.success("Saved"); onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="bg-[#151A23] border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto" data-testid="task-dialog">
        <DialogHeader><DialogTitle className="font-display">{task?.id ? "Edit task" : "New task"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-4 py-2">
          <div className="col-span-2"><Label className="text-white/60 text-xs uppercase tracking-wider">Task name</Label><Input data-testid="task-name-input" value={form.name} onChange={e => set("name", e.target.value)} className="mt-1.5 bg-white/5 border-white/10" /></div>
          <div className="col-span-2"><Label className="text-white/60 text-xs uppercase tracking-wider">Description</Label><Textarea rows={2} value={form.description || ""} onChange={e => set("description", e.target.value)} className="mt-1.5 bg-white/5 border-white/10" /></div>
          <div>
            <Label className="text-white/60 text-xs uppercase tracking-wider">Status</Label>
            <Select value={form.status} onValueChange={v => set("status", v)}><SelectTrigger data-testid="task-status-select" className="mt-1.5 bg-white/5 border-white/10"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#151A23] border-white/10 text-white">{["Not started", "Open", "In Progress", "Blocked", "Done"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-white/60 text-xs uppercase tracking-wider">Priority</Label>
            <Select value={form.priority} onValueChange={v => set("priority", v)}><SelectTrigger className="mt-1.5 bg-white/5 border-white/10"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#151A23] border-white/10 text-white">{["Low", "Normal", "Medium", "High", "Critical"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-white/60 text-xs uppercase tracking-wider">Due date</Label><Input type="date" value={form.due_date || ""} onChange={e => set("due_date", e.target.value)} className="mt-1.5 bg-white/5 border-white/10" /></div>
          <div>
            <Label className="text-white/60 text-xs uppercase tracking-wider">Project</Label>
            <Select value={form.project_id || "__none"} onValueChange={v => set("project_id", v === "__none" ? "" : v)}>
              <SelectTrigger className="mt-1.5 bg-white/5 border-white/10"><SelectValue placeholder="None" /></SelectTrigger>
              <SelectContent className="bg-[#151A23] border-white/10 text-white">
                <SelectItem value="__none">None</SelectItem>
                {projects?.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label className="text-white/60 text-xs uppercase tracking-wider">Assignee (contact)</Label>
            <div className="mt-1.5">
              <ContactPicker
                value={form.assignee_contact_id}
                onChange={(id) => set("assignee_contact_id", id)}
                entityId={entityId}
                projectId={form.project_id || undefined}
              />
            </div>
          </div>
          {/* Optional outreach/research context. Compact, always visible so
              nothing gets hidden behind a toggle. Email + phone are NEVER
              collected here — they live on the linked Contact. */}
          <div className="col-span-2 pt-2 border-t border-white/5">
            <Label className="text-white/60 text-xs uppercase tracking-wider">Reference URL <span className="text-white/30 normal-case font-normal">— optional</span></Label>
            <Input
              type="text"
              inputMode="url"
              placeholder="https://…"
              value={form.reference_url || ""}
              onChange={e => set("reference_url", e.target.value)}
              className="mt-1.5 bg-white/5 border-white/10"
              data-testid="task-reference-url-input"
            />
          </div>
          <div className="col-span-2">
            <Label className="text-white/60 text-xs uppercase tracking-wider">Related contact <span className="text-white/30 normal-case font-normal">— optional</span></Label>
            <div className="mt-1.5" data-testid="task-related-contact-picker">
              <ContactPicker
                value={form.related_contact_id}
                onChange={(id) => set("related_contact_id", id)}
                entityId={entityId}
                projectId={form.project_id || undefined}
              />
            </div>
            <div className="text-[11px] text-white/40 mt-1">
              Save email/phone on the Contact record itself — those details stay in one place.
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)} className="text-white/70 hover:text-white hover:bg-white/10">Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="task-save-btn" className="rounded-full bg-white text-black hover:bg-white/90">{saving ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export { URL_RX, normalizeUrl };
